"use strict";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
Object.defineProperty(exports, "__esModule", { value: true });
exports.connect = connect;
exports.post = post;
exports.closeAll = closeAll;
const utils_1 = require("@aws-amplify/core/internals/utils");
const AWSAppSyncEventsProvider_1 = require("../../Providers/AWSAppSyncEventsProvider");
const appsyncRequest_1 = require("./appsyncRequest");
const utils_2 = require("./utils");
// Keeps a list of open channels in the websocket
const openChannels = new Set();
async function connect(...args) {
    const [ctx, channel, options] = (0, utils_1.resolveCtxArgs)(args);
    const providerOptions = (0, utils_2.configure)(ctx);
    providerOptions.authenticationType = (0, utils_2.normalizeAuth)(options?.authMode, providerOptions.authenticationType);
    providerOptions.apiKey = options?.apiKey || providerOptions.apiKey;
    providerOptions.authToken = options?.authToken || providerOptions.authToken;
    // Pass ctx to the provider so WebSocket auth uses the correct credentials
    await AWSAppSyncEventsProvider_1.AppSyncEventProvider.connect({ ...providerOptions, ctx });
    const channelId = (0, utils_1.amplifyUuid)();
    openChannels.add(channelId);
    let _subscription;
    const sub = (observer, subOptions) => {
        if (!openChannels.has(channelId)) {
            throw new Error('Channel is closed');
        }
        const subscribeOptions = { ...providerOptions, query: channel };
        subscribeOptions.authenticationType = (0, utils_2.normalizeAuth)(subOptions?.authMode, subscribeOptions.authenticationType);
        subscribeOptions.apiKey = subOptions?.apiKey || subscribeOptions.apiKey;
        subscribeOptions.authToken =
            subOptions?.authToken || subscribeOptions.authToken;
        // One-shot readiness signal for THIS subscribe call. The promise state
        // (`settled`/`resolveReady`/`rejectReady`) is kept in locals so the
        // resolve/reject callbacks below close over this specific subscribe call.
        // NOTE: the channel-level `_subscription` closure var is overwritten on
        // each subscribe, so `close()` only rejects the MOST RECENT subscription's
        // `ready`; rejecting `ready` for earlier concurrent subscriptions on the
        // same channel is a pre-existing limitation (not addressed here).
        let settled = false;
        let resolveReady;
        let rejectReady;
        const ready = new Promise((resolve, reject) => {
            resolveReady = resolve;
            rejectReady = reject;
        });
        // Avoid unhandled-rejection noise before the caller reads `.ready`.
        ready.catch(() => undefined);
        const providerSubscription = AWSAppSyncEventsProvider_1.AppSyncEventProvider
            .subscribe({
            ...subscribeOptions,
            ctx,
            onSubscriptionReady: (subscriptionId) => {
                if (!settled) {
                    settled = true;
                    resolveReady({ subscriptionId });
                }
            },
            onSubscriptionError: (_subscriptionId, error) => {
                if (!settled) {
                    settled = true;
                    // `error` is optional on the provider callback; default it so
                    // `ready` never rejects with `undefined`.
                    rejectReady(error ?? new Error('Subscription failed before ready'));
                }
            },
        })
            .subscribe(observer);
        // Expose the readiness promise on a DELEGATING wrapper instead of mutating
        // the rxjs Subscriber returned above. rxjs calls `this.unsubscribe()`
        // internally from Subscriber.error()/complete(); if we patched the
        // subscriber's own `unsubscribe`, a provider-driven error would fire it and
        // reject `ready` with a generic reason, masking the real error. Delegating
        // through a prototype wrapper keeps rxjs's internal `this.unsubscribe()`
        // hitting the ORIGINAL subscriber, so only a CALLER-initiated unsubscribe
        // (via this returned object) or a channel close rejects `ready`. The
        // provider-side onSubscriptionError is the PRIMARY reject signal; this is
        // the fallback for a genuine caller unsubscribe / close-before-ack. rxjs
        // Subscription methods/props (`closed`, `add`, etc.) resolve through the
        // prototype, so the returned object still satisfies the Subscription
        // contract (backward compatible for existing callers).
        const eventsSubscription = Object.create(providerSubscription);
        Object.defineProperty(eventsSubscription, 'unsubscribe', {
            value: () => {
                if (!settled) {
                    settled = true;
                    rejectReady(new Error('Subscription closed before ready'));
                }
                providerSubscription.unsubscribe();
            },
            writable: true,
            enumerable: false,
            configurable: true,
        });
        Object.defineProperty(eventsSubscription, 'ready', {
            value: ready,
            writable: false,
            enumerable: true,
            configurable: true,
        });
        // `close()` unsubscribes the most recent subscription through this closure
        // var (the delegate above), which rejects its `ready` if still pending.
        _subscription = eventsSubscription;
        return eventsSubscription;
    };
    const pub = async (event, pubOptions) => {
        if (!openChannels.has(channelId)) {
            throw new Error('Channel is closed');
        }
        const publishOptions = {
            ...providerOptions,
            query: channel,
            variables: event,
        };
        publishOptions.authenticationType = (0, utils_2.normalizeAuth)(pubOptions?.authMode, publishOptions.authenticationType);
        publishOptions.apiKey = pubOptions?.apiKey || publishOptions.apiKey;
        publishOptions.authToken =
            pubOptions?.authToken || publishOptions.authToken;
        return AWSAppSyncEventsProvider_1.AppSyncEventProvider.publish({ ...publishOptions, ctx });
    };
    const close = async () => {
        _subscription && _subscription.unsubscribe();
        openChannels.delete(channelId);
        setTimeout(() => {
            if (openChannels.size === 0) {
                AWSAppSyncEventsProvider_1.AppSyncEventProvider.closeIfNoActiveSubscription();
            }
        }, 1000);
    };
    return {
        subscribe: sub,
        close,
        publish: pub,
    };
}
async function post(...args) {
    const [ctx, channel, event, options] = (0, utils_1.resolveCtxArgs)(args);
    const providerOptions = (0, utils_2.configure)(ctx);
    providerOptions.authenticationType = (0, utils_2.normalizeAuth)(options?.authMode, providerOptions.authenticationType);
    providerOptions.apiKey = options?.apiKey || providerOptions.apiKey;
    providerOptions.authToken = options?.authToken || providerOptions.authToken;
    // trailing slash required in publish
    const normalizedChannelName = channel[0] === '/' ? channel : `/${channel}`;
    const publishOptions = {
        ...providerOptions,
        query: normalizedChannelName,
        variables: (0, utils_2.serializeEvents)(event),
    };
    const abortController = new AbortController();
    const res = await (0, appsyncRequest_1.appsyncRequest)(ctx, publishOptions, {}, abortController);
    if (res.failed?.length > 0) {
        return res.failed;
    }
}
/**
 * @experimental API may change in future versions
 *
 * Close WebSocket connection, disconnect listeners and reconnect observers
 *
 * @example
 * await events.closeAll()
 *
 * @returns void on success
 * @throws on error
 */
async function closeAll() {
    await AWSAppSyncEventsProvider_1.AppSyncEventProvider.close();
}
